import { translateFn } from '../../../../../utils/translator.helper';

import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';

import { createDialog } from '../../../utils';
import { GzQueryInputWebComponent, GzToastNotificationWebComponent } from '../../../../..';
import { CHANGE_TO_BASIC, YARA_CHARACTERS_LIMIT, YARA_VALIDITY_CHANGED, YARA_VALUE_CHANGED } from '../../../constants';

import { query } from 'lit/decorators.js';
import { YARA_CODE_EDITOR_OPTIONS } from '../types';
import { ConsoleRequest, DataProviderType } from '../../../../../utils/request';
import { InfoBoxSeverity } from '../../../../../ui/gzInfoBox/types';
import { MarkerSeverity, Range } from 'monaco-editor-core';
import { PropertyValues } from 'lit/development';
import { GzToast, ToastPositionX, ToastPositionY, ToastSeverity } from '../../../../../ui/gzToastNotification/type';
import { YARA_ID } from '@nirvana/shared/code-editor';

/**
 * @summary Type here the description of this component
 * @tag gz-yara-definition
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-yara-definition')
export class GzYaraDefinitionWebComponent extends LitElement {
  @query('gz-search-query')
  searchQuery: GzQueryInputWebComponent;

  @property({ attribute: true, reflect: true })
  messageBoxes: { message: string; severity: string }[] = [];

  @property({ attribute: true, reflect: true })
  valid = false;

  @property({ type: Object, attribute: false, reflect: false })
  rule: any = null;

  static get styles() {
    return styles;
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('valid')) {
      this.dispatchEvent(
        new CustomEvent(YARA_VALIDITY_CHANGED, {
          bubbles: true,
          composed: true,
          detail: {
            valid: this.valid,
          },
        })
      );
    }
  }

  render() {
    return html`
      <div class="container mr-2">
        <div class="search-input-container flex-1">
          <gz-search-query
            .enterKeyEnabled=${true}
            .clearBtnString=${translateFn('Clear')}
            .queryInputPlaceholder=${translateFn('Type here')}
            .isVisibleRunQueryButton=${false}
            .helperHref="${this.getKnowledgeLink()}"
            .helpBtnString=${translateFn('Help')}
            .toolTipHelpBtn=${translateFn('Help')}
            .helpBtn=${true}
            .manualResize="${true}"
          >
          </gz-search-query>
        </div>
        ${this.renderBasicButton()}
      </div>
      <div>${this.messageBoxes ? this.renderMessageBox() : nothing}</div>
    `;
  }

  renderBasicButton() {
    if (this.rule._id) {
      // we do not show button if we edit the rule
      return nothing;
    }

    return html` <gz-button
      type="quiet"
      color="primary"
      buttonheight="small"
      buttonfontsize="small"
      lowercase=""
      theme="light"
      class="basic-btn"
      @click="${this.handleBasicButtonClick}"
    >
      ${translateFn('Basic')}
    </gz-button>`;
  }

  renderMessageBox() {
    return this.messageBoxes.map(
      messageBox => html` <gz-info-box severity="${messageBox.severity}"> ${messageBox.message}</gz-info-box> `
    );
  }

  getKnowledgeLink() {
    return 'https://www.bitdefender.com/business/support/en/77209-320064-yara-detection-rules.html';
  }

  async handleBasicButtonClick() {
    const value = await this.searchQuery.getValue();

    if (value.raw) {
      createDialog({
        headline: translateFn('Switch to basic rule definition?'),
        textContent: translateFn('You will lose your current rule query.'),
        confirmButtonText: translateFn('Switch'),
        cancelButtonText: translateFn('Cancel'),
        confirmCallback: () => {
          this.dispatchEvent(
            new CustomEvent(CHANGE_TO_BASIC, {
              bubbles: true,
              cancelable: true,
              composed: true,
            })
          );
        },
      });
      return;
    }

    this.dispatchEvent(
      new CustomEvent(CHANGE_TO_BASIC, {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  constructor() {
    super();

    this.updateComplete.then(() => {
      this.searchQuery.setInputRows(10, 10);
      this.searchQuery.charactersLimit = YARA_CHARACTERS_LIMIT;

      this.searchQuery.setManualResizeMinAndMaxHeight(10, 60, true);
      this.searchQuery.setupCodeEditor(YARA_ID, YARA_CODE_EDITOR_OPTIONS.languageOptions, {
        ...YARA_CODE_EDITOR_OPTIONS.editorOptions,
        theme: 'yaraTheme',
      });

      this.searchQuery.clearButton.disabled = true;
      this.searchQuery.addEventListener('change', (event: CustomEvent) => {
        this.searchQuery.clearButton.disabled = !event.detail.input;
        this.rule.query.yara = event.detail.input;
        this.valid = false;
        if (this.messageBoxes.length !== 0) {
          this.messageBoxes = [];
          this.setMarkers([]);
        }
        this.dispatchEvent(
          new CustomEvent(YARA_VALUE_CHANGED, {
            detail: {
              input: event.detail.input,
              hasError: this.searchQuery.isError,
            },
            bubbles: true,
            composed: true,
          })
        );
      });
      this.searchQuery.setValue(this.rule.query.yara);
    });
  }

  async handleValidation() {
    const value = (await this.searchQuery.getValue())['raw'];

    let response;
    try {
      response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'DetectionRules',
        method: 'checkQuery',
        payload: { query: value },
      });
    } catch (e) {
      console.error(e);
      this.valid = false;
      GzToastNotificationWebComponent.closeAllToasts();
      const toastConfig: GzToast = {
        message: translateFn('Processing error when validating YARA syntax'),
        severity: ToastSeverity.ERROR,
        x: ToastPositionX.RIGHT,
        y: ToastPositionY.BOTTOM,
        autoCloseDelayMs: 5000,
      };
      GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);

      return this.valid;
    }

    if (!response) {
      this.valid = false;
      return this.valid;
    }

    if (response['no_problems'] === 0) {
      this.messageBoxes = [
        {
          message: translateFn('Rule successfully validated.'),
          severity: InfoBoxSeverity.SUCCESS,
        },
      ];

      this.valid = true;
      return this.valid;
    }

    const problems = response['problems'];

    const hasWarnings = problems.some(problem => problem['error_type'] === 'warning');
    const hasErrors = problems.some(problem => problem['error_type'] === 'error');

    const boxes = [] as { message: string; severity: string }[];
    if (hasWarnings) {
      boxes.push({
        message: translateFn("This rule can impact the endpoint's performance. Review the rule and proceed with care."),
        severity: InfoBoxSeverity.WARNING,
      });
    }
    if (hasErrors) {
      boxes.push({
        message: translateFn('Rule errors detected. To proceed to the next step, fix the highlighted errors.'),
        severity: InfoBoxSeverity.ERROR,
      });
    }
    this.setMarkers(problems);
    this.messageBoxes = boxes;

    this.valid = !hasErrors;
    return this.valid;
  }

  setMarkers(errors) {
    const markers = [] as any[];
    const decorations = [] as any[];

    for (const error of errors) {
      const lineNo = error['lineno'] || 1;

      const first = this.searchQuery.getLineFirstNonWhitespaceColumn(lineNo);
      const last = this.searchQuery.getLineLastNonWhitespaceColumn(lineNo);

      const severity = error['error_type'] === 'error' ? MarkerSeverity.Error : MarkerSeverity.Warning;
      const messagePrefix = error['error_type'] === 'error' ? 'Error' : 'Warning';
      const message = `${messagePrefix}: ${error['message']}`;
      markers.push({
        severity: severity,
        startLineNumber: lineNo,
        endLineNumber: lineNo,
        startColumn: first,
        endColumn: last,
        message,
      });

      decorations.push({
        range: new Range(lineNo, first, lineNo, last),
        options: {
          isWholeLine: true,
          glyphMarginClassName:
            error['error_type'] === 'error'
              ? 'glyph-error ds-icon-notification_negative'
              : 'glyph-warning ds-icon-notification_warning',
          glyphMarginHoverMessage: {
            value: message,
          },
        },
      });
    }

    this.searchQuery.setModelMarkers(markers);
    this.searchQuery.setDecorations(decorations);
  }

  async getValue(): Promise<string> {
    return (await this.searchQuery.getValue())['raw'];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-yara-definition': GzYaraDefinitionWebComponent;
  }
}
